import { Cached } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';
import { type ReactElement, useContext, useEffect, useMemo } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import type { ProjectProduct } from '@core/types/api/ProjectProduct/ProjectProduct';
import { toRecordById } from '@core/utils/domainHelpers';
import { hasStringValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  applyProjectPricesAsOriginal,
  flattenProductsByPlants,
  projectProductsToProductsByPlants,
} from '@core/utils/quoteHelpers';
import { isValidUUID } from '@core/utils/uuid';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import type { ProductSectionProps } from '@web/components/ProductSection/ProductSection';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';

import { QuoteDrawerMode, useQuoteDrawerContext } from '../QuoteDrawer';
import ProductsByPlantSection from './ProductsByPlantSection';

export type ProductsSectionProps = {
  curProductsRef: ProductSectionProps['curProductsRef'];
  mode?: QuoteDrawerMode;
};

export const ProductsSection = ({
  curProductsRef,
  mode = QuoteDrawerMode.Full,
}: ProductsSectionProps): ReactElement => {
  const { readOnly } = useQuoteDrawerContext();
  const formikBag = useFormikContext<QuoteFormikType>();
  const { defaultIsMoneySwitchOn, marginConfiguration, defaultMargins } = useTenantMargins();
  const { userInfo } = useContext(SlabContext);
  const hasTierPricing =
    userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    userInfo.tenant.tierPricing;
  const companyId = formikBag.values.company?.id ?? null;
  const { data: fixedPricingByCompany } = useSlabQuery(
    'GET tier pricing by company ID',
    { queryParams: { companyIds: [companyId ?? ''] } },
    { enabled: hasTierPricing && hasStringValue(companyId) },
  );
  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useSlabQuery(
    'GET project by ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const {
    isLoading: isLoadingProjectProducts,
    isError: isErrorProjectProducts,
    data: projectProducts,
  } = useSlabQuery(
    'GET project products by project ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const projectProductsByProductID = useMemo(() => {
    if (formikBag.values.project.id === null) {
      return {};
    }
    const byProductID: Record<string, ProjectProduct> = {};
    projectProducts?.forEach((pp) => {
      byProductID[pp.product.id] = pp;
    });
    return byProductID;
  }, [projectProducts]);

  // FormikQuote seeds originalPrice from a project-price fetch that falls back to the catalog price
  // when it fails; re-seed from this section's own query so tier pricing uses the project base.
  useEffect(() => {
    if (!isValidUUID(formikBag.values.id) || !projectProducts?.length) return;
    SetFormikValue(
      formikBag,
      'mainProductsByPlants',
      applyProjectPricesAsOriginal(formikBag.values.mainProductsByPlants ?? [], projectProducts),
    );
  }, [projectProducts]);

  if (mode !== QuoteDrawerMode.Quick && formikBag.values.project.id === null) {
    return (
      <Box>
        <Typography variant='h5'>Please select a project.</Typography>
      </Box>
    );
  }

  const isQuickModeNoProject =
    mode === QuoteDrawerMode.Quick && formikBag.values.project.id === null;

  if (!isQuickModeNoProject && (isLoadingProject || isLoadingProjectProducts)) {
    return <LoadingSpinner />;
  }

  if (
    formikBag.values.project.id !== null &&
    (isErrorProject ||
      project === undefined ||
      isErrorProjectProducts ||
      projectProducts === undefined)
  ) {
    return (
      <Box>
        <Typography variant='h5'>Error loading products.</Typography>
      </Box>
    );
  }

  return (
    <>
      {!isQuickModeNoProject && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          {!readOnly && (
            <Tooltip
              title='Clears the current products and reloads from project'
              placement='top-start'
            >
              <Button
                onClick={(): void => {
                  if (project === undefined || projectProducts === undefined) return;
                  const newFormikMainProductsByPlants = projectProductsToProductsByPlants(
                    project,
                    projectProducts, // TODO: investigate why in some cases project.products empty and we need to go this way
                    defaultIsMoneySwitchOn,
                    marginConfiguration,
                    defaultMargins,
                    fixedPricingByCompany,
                  );
                  SetFormikValue(formikBag, 'mainProductsByPlants', newFormikMainProductsByPlants);

                  // Update curProductsRef to match the new products
                  const flattenedProducts = flattenProductsByPlants(newFormikMainProductsByPlants);
                  const validFormikProducts = flattenedProducts.filter(
                    (fp): fp is typeof fp & { product: { id: string } } =>
                      fp.product.id !== null &&
                      projectProductsByProductID[fp.product.id]?.product !== undefined,
                  );

                  const newProducts = toRecordById(
                    validFormikProducts,
                    (fp) => projectProductsByProductID[fp.product.id].product,
                  );
                  curProductsRef.current = {
                    ...curProductsRef.current,
                    ...newProducts,
                  };
                }}
              >
                <Cached />
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      <ProductsByPlantSection
        sectionType='main'
        curProductsRef={curProductsRef}
        project={isQuickModeNoProject ? undefined : project}
        projectProductsByProductID={isQuickModeNoProject ? {} : projectProductsByProductID}
      />
    </>
  );
};
